/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { createPortal } from "react-dom";

type PrimitiveProps<T extends HTMLElement> = React.HTMLAttributes<T> & {
  asChild?: boolean;
  align?: string;
  side?: string;
  avoidCollisions?: boolean;
  sideOffset?: number;
  container?: HTMLElement | null;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  [key: string]: unknown;
};

const PopoverContext = React.createContext<{ open: boolean; setOpen: (open: boolean) => void; triggerRef: React.RefObject<HTMLElement> } | null>(null);

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref && "current" in ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

function popoverNumber(value: unknown, defaultValue: number) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return defaultValue;
}

function popoverSide(value: unknown) {
  if (value === "top" || value === "right" || value === "left") {
    return value;
  }
  return "bottom";
}

function popoverAlign(value: unknown) {
  if (value === "start" || value === "end") {
    return value;
  }
  return "center";
}

function clampPopoverCoordinate(value: number, min: number, max: number) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function activePopoverTrigger(context: { triggerRef: React.RefObject<HTMLElement> } | null) {
  if (context?.triggerRef.current) {
    return context.triggerRef.current;
  }
  const expandedTrigger = document.querySelector('[data-slot="popover-trigger"][aria-expanded="true"]');
  if (expandedTrigger instanceof HTMLElement) {
    return expandedTrigger;
  }
  return null;
}

function createPrimitive<T extends HTMLElement>(tag: keyof JSX.IntrinsicElements, displayName: string) {
  const Component = React.forwardRef<T, PrimitiveProps<T>>(({ children, asChild, ...props }, ref) => {
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, { ...props, ref } as React.HTMLAttributes<T>);
    }
    return React.createElement(tag, { ...props, ref }, children as React.ReactNode);
  });
  Component.displayName = displayName;
  return Component;
}

type PopoverRootProps = {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  [key: string]: unknown;
};

const PopoverRoot: React.FC<PopoverRootProps> = ({ children, open, defaultOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen === true);
  const triggerRef = React.useRef<HTMLElement>(null);
  const isControlled = open !== undefined;
  const currentOpen = open ?? internalOpen;
  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return <PopoverContext.Provider value={{ open: currentOpen, setOpen, triggerRef }}>{children}</PopoverContext.Provider>;
};
PopoverRoot.displayName = "PopoverRoot";

const PopoverTrigger = React.forwardRef<HTMLButtonElement, PrimitiveProps<HTMLButtonElement>>(({ children, asChild, onClick, ...props }, ref) => {
  const context = React.useContext(PopoverContext);
  const triggerOnClick = onClick as React.MouseEventHandler<HTMLButtonElement> | undefined;
  const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
    triggerOnClick?.(event as React.MouseEvent<HTMLButtonElement>);
    if (event.defaultPrevented) return;
    context?.setOpen(!context.open);
  };

  if (asChild && React.isValidElement(children)) {
    const childProps = children.props as { onClick?: React.MouseEventHandler<HTMLElement> };
    const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
    return React.cloneElement(children, {
      ...props,
      ref: (node: HTMLElement | null) => {
        assignRef(context?.triggerRef, node);
        assignRef(childRef, node);
        assignRef(ref as React.Ref<HTMLElement>, node);
      },
      "aria-expanded": context?.open,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        childProps.onClick?.(event);
        if (event.defaultPrevented) return;
        handleClick(event);
      },
    } as React.HTMLAttributes<HTMLElement>);
  }

  return React.createElement(
    "button",
    {
      ...props,
      ref: (node: HTMLButtonElement | null) => {
        assignRef(context?.triggerRef, node);
        assignRef(ref, node);
      },
      type: (props.type as string | undefined) ?? "button",
      "aria-expanded": context?.open,
      onClick: handleClick,
    },
    children as React.ReactNode,
  );
});
PopoverTrigger.displayName = "PopoverTrigger";

type PopoverFixedStyle = React.CSSProperties & {
  "--radix-popover-trigger-width"?: string;
  "--radix-popover-content-transform-origin"?: string;
};

const PopoverContent = React.forwardRef<HTMLDivElement, PrimitiveProps<HTMLDivElement>>(
  ({ children, asChild, ...props }, ref) => {
    const context = React.useContext(PopoverContext);
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const [fixedStyle, setFixedStyle] = React.useState<PopoverFixedStyle>({
      position: "fixed",
      left: 0,
      top: 0,
      zIndex: 130,
      visibility: "hidden",
    });
    const {
      align,
      side,
      sideOffset,
      container,
      avoidCollisions,
      onOpenAutoFocus,
      onCloseAutoFocus,
      collisionPadding,
      style,
      ...contentProps
    } = props;
    const contentStyle = style as React.CSSProperties | undefined;
    void container;
    void avoidCollisions;
    void onOpenAutoFocus;
    void onCloseAutoFocus;

    React.useLayoutEffect(() => {
      if (context && !context.open) return;
      const content = localRef.current;
      if (!content) return;
      const resolvedSide = popoverSide(side);
      const resolvedAlign = popoverAlign(align);
      const offset = popoverNumber(sideOffset, 4);
      const padding = popoverNumber(collisionPadding, 8);

      const updatePosition = () => {
        const trigger = activePopoverTrigger(context);
        if (!trigger) return;
        const triggerRect = trigger.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        let left = triggerRect.left + (triggerRect.width - contentRect.width) / 2;
        if (resolvedAlign === "start") {
          left = triggerRect.left;
        }
        if (resolvedAlign === "end") {
          left = triggerRect.right - contentRect.width;
        }
        let top = triggerRect.bottom + offset;
        if (resolvedSide === "top") {
          top = triggerRect.top - contentRect.height - offset;
        }
        if (resolvedSide === "left" || resolvedSide === "right") {
          top = triggerRect.top + (triggerRect.height - contentRect.height) / 2;
        }
        if (resolvedSide === "left") {
          left = triggerRect.left - contentRect.width - offset;
        }
        if (resolvedSide === "right") {
          left = triggerRect.right + offset;
        }
        const maxLeft = window.innerWidth - contentRect.width - padding;
        const maxTop = window.innerHeight - contentRect.height - padding;
        setFixedStyle({
          ...contentStyle,
          "--radix-popover-trigger-width": `${Math.round(triggerRect.width)}px`,
          "--radix-popover-content-transform-origin": `${resolvedAlign} ${resolvedSide}`,
          position: "fixed",
          left: clampPopoverCoordinate(left, padding, maxLeft),
          top: clampPopoverCoordinate(top, padding, maxTop),
          zIndex: typeof contentStyle?.zIndex === "number" ? contentStyle.zIndex : 130,
          visibility: "visible",
        });
      };

      updatePosition();
      const frame = window.requestAnimationFrame(updatePosition);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.cancelAnimationFrame(frame);
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }, [align, collisionPadding, context, side, sideOffset, contentStyle]);

    React.useEffect(() => {
      if (!context?.open) return;
      const closeOnOutsidePointer = (event: PointerEvent) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        const content = localRef.current;
        const trigger = activePopoverTrigger(context);
        if (content?.contains(target)) return;
        if (trigger?.contains(target)) return;
        context.setOpen(false);
      };
      const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          context.setOpen(false);
        }
      };
      document.addEventListener("pointerdown", closeOnOutsidePointer, true);
      document.addEventListener("keydown", closeOnEscape, true);
      return () => {
        document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
        document.removeEventListener("keydown", closeOnEscape, true);
      };
    }, [context]);

    if (context && !context.open) return null;
    const setContentRef = (node: HTMLDivElement | null) => {
      localRef.current = node;
      assignRef(ref, node);
    };
    if (asChild && React.isValidElement(children)) {
      const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLDivElement> }).ref;
      return React.cloneElement(children, {
        ...contentProps,
        ref: (node: HTMLDivElement | null) => {
          setContentRef(node);
          assignRef(childRef, node);
        },
        style: fixedStyle,
      } as React.HTMLAttributes<HTMLDivElement>);
    }
    return React.createElement("div", { ...contentProps, ref: setContentRef, style: fixedStyle, "data-state": context?.open === true ? "open" : undefined }, children as React.ReactNode);
  },
);
PopoverContent.displayName = "PopoverContent";

const PopoverPortal: React.FC<{ children?: React.ReactNode; container?: HTMLElement | null }> = ({ children, container }) => {
  if (typeof document === "undefined") return <>{children}</>;
  const target = container ?? document.body;
  return createPortal(children, target);
};

export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Portal: PopoverPortal,
  Anchor: createPrimitive<HTMLDivElement>("div", "PopoverAnchor"),
};
